import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogOut, LayoutDashboard, BookOpen, Award, User as UserIcon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { COMPANY } from "@/lib/company";
import { useAuth } from "@/context/AuthContext";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/projects", label: "Projects" },
    { to: "/careers", label: "Careers" },
    { to: "/contact", label: "Contact" },
];

const AUTH_MENU = [
    { to: "/portal/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/portal/courses", label: "Catalog", icon: BookOpen },
    { to: "/portal/certificates", label: "Certificates", icon: Award },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, user, logout } = useAuth();
    const { isAdminAuthenticated } = useAdminAuth();
    const adminHref = isAdminAuthenticated ? "/admin" : "/admin/login";
    const adminLabel = isAdminAuthenticated ? "Admin Console" : "Admin";
    const menuRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        if (!menuOpen) return;
        const onClickOut = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOut);
        return () => document.removeEventListener("mousedown", onClickOut);
    }, [menuOpen]);

    const initials = (user?.full_name || "?")
        .split(/\s+/)
        .map((n) => n[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

    const handleLogout = () => {
        // Navigate BEFORE clearing auth so the current protected route doesn't
        // get a chance to redirect to /portal/login on the next render.
        navigate("/", { replace: true });
        setMenuOpen(false);
        // Defer logout to the next tick so navigation commits first.
        setTimeout(() => logout(), 0);
    };

    return (
        <header
            className={`sticky top-0 z-40 border-b transition-colors duration-300 ${
                scrolled
                    ? "border-brand-line bg-brand-offwhite/95 backdrop-blur"
                    : "border-transparent bg-brand-offwhite"
            }`}
            data-testid="site-header"
        >
            <div className="container-x flex h-[72px] items-center justify-between">
                <Link
                    to="/"
                    className="group flex items-center gap-3"
                    data-testid="header-logo"
                    aria-label="EB Construction home"
                >
                    <img
                        src="/brand/logo-horizontal.png?v=3"
                        alt="EB Construction — ElevateBuild Construction Services Ltd"
                        className="h-9 w-auto md:h-11"
                    />
                </Link>

                <nav
                    className="hidden items-center gap-9 lg:flex"
                    data-testid="header-nav-desktop"
                >
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) =>
                                `link-underline font-mono text-[11px] uppercase tracking-[0.2em] ${
                                    isActive
                                        ? "text-brand-gold"
                                        : "text-brand-charcoal hover:text-brand-gold"
                                }`
                            }
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}

                    {isAuthenticated ? (
                        <div className="relative" ref={menuRef}>
                            <button
                                type="button"
                                onClick={() => setMenuOpen((v) => !v)}
                                className="flex items-center gap-2 border border-brand-charcoal px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-brand-offwhite"
                                data-testid="header-user-menu-toggle"
                            >
                                <span className="grid h-6 w-6 place-items-center bg-brand-gold font-mono-tag text-brand-charcoal">
                                    {initials}
                                </span>
                                Portal
                            </button>
                            <AnimatePresence>
                                {menuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        transition={{ duration: 0.18 }}
                                        className="absolute right-0 top-[calc(100%+8px)] w-64 border border-brand-line bg-white p-4 shadow-lg"
                                        data-testid="header-user-menu"
                                    >
                                        <p className="font-display text-lg font-semibold leading-tight">
                                            {user?.full_name}
                                        </p>
                                        <p className="mt-1 truncate font-mono-tag text-brand-steel">
                                            {user?.email}
                                        </p>
                                        <div className="my-4 h-px bg-brand-line" />
                                        <ul className="flex flex-col gap-1">
                                            {AUTH_MENU.map((m) => {
                                                const Icon = m.icon;
                                                return (
                                                    <li key={m.to}>
                                                        <Link
                                                            to={m.to}
                                                            className="flex items-center gap-2 py-2 font-body text-sm text-brand-charcoal hover:text-brand-gold"
                                                            data-testid={`header-menu-${m.label.toLowerCase()}`}
                                                        >
                                                            <Icon className="h-4 w-4" />
                                                            {m.label}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <div className="my-4 h-px bg-brand-line" />
                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="flex w-full items-center justify-between py-2 font-mono-tag text-brand-charcoal hover:text-brand-gold"
                                            data-testid="header-logout"
                                        >
                                            LOG OUT
                                            <LogOut className="h-4 w-4" />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <Link
                            to="/portal"
                            className="group inline-flex items-center gap-2 border border-brand-charcoal px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-brand-offwhite"
                            data-testid="nav-link-portal"
                        >
                            Training Portal
                            <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    )}

                    <NavLink
                        to={adminHref}
                        className={({ isActive }) =>
                            `link-underline font-mono text-[11px] uppercase tracking-[0.2em] ${
                                isActive
                                    ? "text-brand-gold"
                                    : "text-brand-charcoal hover:text-brand-gold"
                            }`
                        }
                        data-testid="header-admin-nav"
                    >
                        {adminLabel}
                    </NavLink>
                </nav>

                <button
                    type="button"
                    className="grid h-11 w-11 place-items-center border border-brand-charcoal text-brand-charcoal lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-brand-line bg-brand-offwhite lg:hidden"
                        data-testid="mobile-menu-panel"
                    >
                        <div className="container-x flex flex-col gap-1 py-6">
                            {NAV.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `flex items-center justify-between border-b border-brand-line py-4 font-display text-3xl uppercase tracking-wide ${
                                            isActive
                                                ? "text-brand-gold"
                                                : "text-brand-charcoal"
                                        }`
                                    }
                                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                    <ArrowUpRight className="h-5 w-5" />
                                </NavLink>
                            ))}

                            {isAuthenticated ? (
                                <>
                                    <p className="mt-4 font-mono-tag text-brand-gold">
                                        {user?.full_name}
                                    </p>
                                    {AUTH_MENU.map((m) => (
                                        <Link
                                            key={m.to}
                                            to={m.to}
                                            className="mt-2 flex items-center justify-between bg-white px-5 py-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal"
                                            data-testid={`mobile-portal-${m.label.toLowerCase()}`}
                                        >
                                            {m.label}
                                            <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        className="mt-4 flex items-center justify-between bg-brand-charcoal px-5 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-gold"
                                        data-testid="mobile-header-logout"
                                    >
                                        Log out
                                        <LogOut className="h-4 w-4" />
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/portal"
                                    className="mt-4 flex items-center justify-between bg-brand-charcoal px-5 py-4 font-mono text-xs uppercase tracking-[0.25em] text-brand-gold"
                                    data-testid="mobile-nav-link-portal"
                                >
                                    Training Portal
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            )}

                            <NavLink
                                to={adminHref}
                                className={({ isActive }) =>
                                    `flex items-center justify-between border-b border-brand-line py-4 font-display text-3xl uppercase tracking-wide ${
                                        isActive
                                            ? "text-brand-gold"
                                            : "text-brand-charcoal"
                                    }`
                                }
                                data-testid="mobile-admin-nav"
                            >
                                {adminLabel}
                                <ArrowUpRight className="h-5 w-5" />
                            </NavLink>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
